import { TDeviceScreen } from '@/services';
import { getTabletRouteFromScreen } from '@/utils/device-route.util';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDeviceCheck } from './useDeviceCheck.hook';

/**
 * Gate a tablet screen against the `/device/client/check` response.
 *
 * Each TAB-xx page has an expected `screen` value (REGISTER for TAB-01,
 * INACTIVE_NOTICE for TAB-02, LOGIN for TAB-03). If the server's response
 * says otherwise — e.g. user lands on /tab-02 but the device is already
 * ACTIVE on an office IP → screen=LOGIN — redirect via
 * `getTabletRouteFromScreen`.
 *
 * Returns the underlying `useDeviceCheck()` result plus an `isReady` flag.
 * `isReady` stays false during the tick where the check has resolved but the
 * guard's effect is about to navigate away — callers use it to render a
 * loader instead of the page content, avoiding a flash of the wrong UI
 * before the redirect lands.
 */
export const useTabletScreenGuard = (
	expected: TDeviceScreen | TDeviceScreen[]
) => {
	const check = useDeviceCheck();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const { screen, isChecking, isLoading, error } = check;

	const expectedList = useMemo(
		() => (Array.isArray(expected) ? expected : [expected]),
		[expected]
	);

	// Will the effect below navigate away on the next tick? If so, callers
	// should keep the loader up instead of rendering the page content.
	const willNavigate =
		!isChecking &&
		!isLoading &&
		!error &&
		!!screen &&
		!expectedList.includes(screen) &&
		getTabletRouteFromScreen(screen) !== pathname;

	const isReady = !isChecking && !isLoading && !willNavigate;

	useEffect(() => {
		// Wait for the check to finish before deciding.
		if (isChecking || isLoading) return;
		// On network / fingerprint error, stay put — DeviceRouteHandler already
		// falls back to TAB-01 on error, so nothing else to do.
		if (error) return;
		if (!screen) return;
		if (expectedList.includes(screen)) return;

		const target = getTabletRouteFromScreen(screen);
		if (target === pathname) return;

		navigate(target, { replace: true });
	}, [screen, isChecking, isLoading, error, expectedList, navigate, pathname]);

	return { ...check, isReady };
};

export default useTabletScreenGuard;
